import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { SKILLS_CREATOR_LANDING_URL } from '../../../utils/brand';
import {
  applyTypography,
  useAuth,
  useLoginRequiredModal,
  trackCtaClick,
  BaseButton,
  GoogleButton,
  useIsMobile,
  useT,
  useLang,
} from '@repo/common';
import { ReactComponent as ArrowRightIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-right.svg';

const FONT_MONO = "'IBM Plex Mono', ui-monospace, 'SF Mono', Menlo, monospace";
const FONT_SERIF = "'Instrument Serif', ui-serif, Georgia, serif";

const AUDIENCES = [
  { key: 'user', label: 'For User' },
  { key: 'creator', label: 'For Business'},
  // 이 탭만 화면을 바꾸지 않고 Koready Skills 크리에이터 랜딩으로 보낸다
  { key: 'company', label: 'For Creator', href: SKILLS_CREATOR_LANDING_URL },
];

const goToAudienceLink = (audience) => {
  trackCtaClick({
    button_name: 'home_tab_for_creator',
    section: 'home_audience_tabs',
    page_path: window.location.pathname,
  });
  window.location.assign(audience.href);
};


const DATA = [
  { name: 'AVA', flag: '🇺🇸', from: 0, to: 0 },
  { name: 'LINH', flag: '🇻🇳', from: 1, to: 2 },
  { name: 'ARJUN', flag: '🇮🇳', from: 2, to: 1 },
  { name: 'LUCAS', flag: '🇧🇷', from: 3, to: 3 },
  { name: 'EMMA', flag: '🇩🇪', from: 4, to: 2 },
  { name: 'YUKI', flag: '🇯🇵', from: 5, to: 0 },
  { name: 'MIA', flag: '🇵🇭', from: 2, to: 3 },
  { name: 'NOAH', flag: '🇫🇷', from: 4, to: 1 },
];
const LY = [90, 205, 320, 435];
const LYL = [60, 140, 220, 300, 380, 460];
const MFX = [105, 295, 105, 295, 105, 295];
const MFYB = [84, 84, 130, 130, 176, 176];
const MTX = [105, 295, 105, 295];
const MTYC = [500, 500, 546, 546];
const STAGES = ['EVALUATING', 'SCORING', 'VERIFYING', 'GUIDING'];

const D_TRAITS = [
  { top: 38, color: '#ffb59d', label: 'No visa plan' },
  { top: 118, color: '#a8c1f0', label: 'No portfolio' },
  { top: 198, color: '#a7e3c4', label: 'TOPIK 3' },
  { top: 278, color: '#e8d59a', label: 'Zero KR experience' },
  { top: 358, color: '#cdb8ec', label: 'International student' },
  { top: 438, color: '#b8dce8', label: 'No interview practice' },
];
const D_OUTCOMES = [
  { top: 68, label: 'Readiness Score 72' },
  { top: 183, label: 'Skill verified ✓' },
  { top: 298, label: 'KR-ready resume' },
  { top: 413, label: 'Interview ready' },
];
const M_TRAITS = [
  { left: 30, top: 48, color: '#ffb59d', label: 'No visa plan' },
  { left: 220, top: 48, color: '#a8c1f0', label: 'No portfolio' },
  { left: 30, top: 94, color: '#a7e3c4', label: 'TOPIK 3' },
  { left: 220, top: 94, color: '#e8d59a', label: 'Zero KR experience' },
  { left: 30, top: 140, color: '#cdb8ec', label: 'Intl student' },
  { left: 220, top: 140, color: '#b8dce8', label: 'No interview practice' },
];
const M_OUTCOMES = [
  { left: 30, top: 482, label: 'Readiness Score 72' },
  { left: 220, top: 482, label: 'Skill verified ✓' },
  { left: 30, top: 528, label: 'KR-ready resume' },
  { left: 220, top: 528, label: 'Interview ready' },
];
const D_PATHS = [
  'M250,60 C360,60 420,262 494,262',
  'M250,140 C360,140 420,262 494,262',
  'M250,220 C360,220 420,262 494,262',
  'M250,300 C360,300 420,262 494,262',
  'M250,380 C360,380 420,262 494,262',
  'M250,460 C360,460 420,262 494,262',
  'M666,262 C760,262 820,90 910,90',
  'M666,262 C760,262 820,205 910,205',
  'M666,262 C760,262 820,320 910,320',
  'M666,262 C760,262 820,435 910,435',
];
const M_PATHS = [
  'M105,84 C105,150 200,170 200,232',
  'M295,84 C295,150 200,170 200,232',
  'M105,130 C105,175 200,185 200,232',
  'M295,130 C295,175 200,185 200,232',
  'M105,176 C105,205 200,208 200,232',
  'M295,176 C295,205 200,208 200,232',
  'M200,368 C200,425 105,435 105,482',
  'M200,368 C200,425 295,435 295,482',
  'M200,368 C200,470 105,480 105,528',
  'M200,368 C200,470 295,480 295,528',
];
const ENGINE_LOGO =
  'M161.368 40.239C174.815 24.8373 213.812 -4.64854 262.223 0.622804C310.633 5.8943 345.148 46.8342 356.354 66.6453H425.614L399.871 40.9041L429.915 10.8621L519.998 100.937L429.914 191.012L399.871 160.971L441.379 119.467H369.802C374.284 137.074 369.802 185.768 356.354 211.904C334.448 254.481 295.841 284.533 262.223 284.533C228.605 284.533 208.434 258.122 208.434 225.109C208.434 192.096 228.605 119.467 302.564 86.4539C295.841 75.4494 277.015 53.4402 255.499 53.4402C228.604 53.4402 188.263 53.4403 174.815 132.672C164.058 196.057 143.439 339.555 134.474 403.381L73.96 396.778C89.6483 299.941 121.025 115.51 121.026 99.6589C121.026 79.851 114.302 60.0427 100.854 60.0427C87.4075 60.0429 80.6835 65.974 73.96 82.8162C68.5811 96.2899 62.7539 108.462 60.5127 112.864L0 93.0466C13.4473 60.0335 51.2634 0.639248 100.854 0.62671C134.473 0.61847 161.368 33.6319 161.368 40.239ZM325.388 122.038C309.998 114.065 283.701 134.275 266.652 167.178C249.604 200.081 248.259 233.218 263.649 241.191C279.04 249.164 305.336 228.953 322.385 196.051C339.433 163.148 340.778 130.011 325.388 122.038Z';

class PipelineEngine {
  constructor() {
    this.layers = {};
    this.chips = { A: [], M: [] };
    this.clock = 0;
    this.totalSteps = 0;
    this.stage = 0;
    this.orbitRot = 0;
    this.rm = false;
    this.hubGlow = null;
    this.hubGlowM = null;
    this.props = {
      engine: 'Quadrant sweep',
      speed: 1.2,
      paused: false,
      accent: '#1F8A5B',
    };
    this.engines = {
      A: {
        R: 96,
        discR: 86,
        orbitRad: 63,
        labelFs: 8,
        slotW: 108,
        slotH: 14,
        wordFs: 10,
        coreR: 36,
        qRad: 52,
        qFs: 7,
      },
      M: {
        R: 78,
        discR: 70,
        orbitRad: 51,
        labelFs: 7,
        slotW: 90,
        slotH: 12,
        wordFs: 9,
        coreR: 30,
        qRad: 42,
        qFs: 6.5,
      },
    };
  }

  get mode() {
    const m = this.props.engine ?? 'Quadrant sweep';
    return m === 'Vertical ticker' ? 'Dartboard spin' : m;
  }

  cycleSteps() {
    return this.totalSteps === 0 ? 0 : ((this.totalSteps - 1) % 4) + 1;
  }

  setEnginePart(k, part, el) {
    const e = this.engines[k];
    if (!el || e[part] === el) return;
    e[part] = el;
    if (e.layer && e.slot && e.disc) this.buildEngine(e);
  }

  buildEngine(e) {
    e.builtMode = this.mode;
    if (e.timer) {
      clearTimeout(e.timer);
      e.timer = null;
    }
    e.layer.innerHTML = '';
    e.slot.innerHTML = '';
    e.slot.style.cssText =
      'position:relative;width:' + e.slotW + 'px;height:' + e.slotH + 'px;';
    e.sweepVal = e.sweepFrom = e.sweepTarget = this.cycleSteps();
    e.wrapped = false;
    const m = this.mode;
    if (m === 'Orbital dial') {
      const spring = 'transform .8s cubic-bezier(.3,1.25,.45,1)';
      const orbit = document.createElement('div');
      orbit.style.cssText =
        'position:absolute;inset:0;' +
        (this.rm ? '' : 'transition:' + spring + ';');
      e.orbit = orbit;
      e.orbitLabels = STAGES.map((s, i) => {
        const wrap = document.createElement('div');
        wrap.style.cssText =
          'position:absolute;left:50%;top:50%;width:0;height:0;transform:rotate(' +
          i * 90 +
          'deg) translateY(-' +
          e.orbitRad +
          'px);';
        const t = document.createElement('div');
        t.textContent = s;
        t.style.cssText =
          'position:absolute;left:0;top:0;white-space:nowrap;font-size:' +
          e.labelFs +
          'px;font-weight:500;letter-spacing:.16em;transition:' +
          (this.rm ? '' : spring + ',') +
          'color .4s,opacity .4s;';
        wrap.appendChild(t);
        orbit.appendChild(wrap);
        return t;
      });
      e.layer.appendChild(orbit);
      this.applyOrbital(e);
    } else if (m === 'Progress arc') {
      const NS = 'http://www.w3.org/2000/svg';
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('width', e.R * 2);
      svg.setAttribute('height', e.R * 2);
      svg.style.cssText = 'position:absolute;inset:0;transform:rotate(-90deg);';
      const c = document.createElementNS(NS, 'circle');
      const r = e.R - 0.5,
        C = 2 * Math.PI * r;
      c.setAttribute('cx', e.R);
      c.setAttribute('cy', e.R);
      c.setAttribute('r', r);
      c.setAttribute('fill', 'none');
      c.setAttribute('stroke', '#3E9B63');
      c.setAttribute('stroke-width', '2');
      c.setAttribute('stroke-linecap', 'round');
      c.style.strokeDasharray = C;
      c.style.strokeDashoffset = C * (1 - this.cycleSteps() / 4);
      if (!this.rm)
        c.style.transition = 'stroke-dashoffset .9s cubic-bezier(.25,.8,.3,1)';
      svg.appendChild(c);
      e.layer.appendChild(svg);
      e.arc = c;
      e.arcC = C;
      e.wordA = this.makeWord(e, STAGES[this.stage]);
      e.wordB = this.makeWord(e, '');
      e.wordA.style.transition = e.wordB.style.transition = 'opacity .45s';
      e.wordB.style.opacity = '0';
      e.slot.appendChild(e.wordA);
      e.slot.appendChild(e.wordB);
    } else if (m === 'Dartboard spin') {
      const NS = 'http://www.w3.org/2000/svg';
      const c = e.R;
      const board = document.createElement('div');
      board.style.cssText =
        'position:absolute;inset:0;will-change:transform;' +
        (this.rm
          ? ''
          : 'transition:transform 3.6s cubic-bezier(.34,1.05,.35,1);');
      const svg = document.createElementNS(NS, 'svg');
      svg.setAttribute('width', c * 2);
      svg.setAttribute('height', c * 2);
      svg.style.cssText = 'position:absolute;inset:0;';
      e.dartSectors = [0, 1, 2, 3].map((i) => {
        const p = document.createElementNS(NS, 'path');
        p.setAttribute(
          'd',
          this.sectorPath(c, e.discR, i * 90 - 45, i * 90 + 45),
        );
        p.setAttribute('stroke', '#D8D3C8');
        p.setAttribute('stroke-width', '1');
        if (!this.rm) p.style.transition = 'fill .4s';
        svg.appendChild(p);
        return p;
      });
      const ring = document.createElementNS(NS, 'circle');
      ring.setAttribute('cx', c);
      ring.setAttribute('cy', c);
      ring.setAttribute('r', e.discR - 8);
      ring.setAttribute('fill', 'none');
      ring.setAttribute('stroke', '#D8D3C8');
      ring.setAttribute('stroke-dasharray', '2 4');
      svg.appendChild(ring);
      e.dartLabels = [0, 1, 2, 3].map((i) => {
        const g = document.createElementNS(NS, 'g');
        g.setAttribute(
          'transform',
          'rotate(' + i * 90 + ' ' + c + ' ' + c + ')',
        );
        const t = document.createElementNS(NS, 'text');
        t.setAttribute('x', c);
        t.setAttribute('y', c - e.discR + 15);
        t.setAttribute('text-anchor', 'middle');
        t.setAttribute('font-size', e.qFs);
        t.setAttribute('letter-spacing', '.1em');
        t.setAttribute('font-family', "ui-monospace,'SF Mono',Menlo,monospace");
        t.textContent = STAGES[i];
        g.appendChild(t);
        svg.appendChild(g);
        return t;
      });
      board.appendChild(svg);
      e.board = board;
      e.layer.appendChild(board);
      const cr = e.coreR + 16;
      const core = document.createElement('div');
      core.style.cssText =
        'position:absolute;left:' +
        (c - cr) +
        'px;top:' +
        (c - cr) +
        'px;width:' +
        cr * 2 +
        'px;height:' +
        cr * 2 +
        'px;box-sizing:border-box;border-radius:50%;background:#FBFAF7;border:1px solid #E3DFD5;box-shadow:0 2px 12px rgba(32,29,26,.07);';
      e.layer.appendChild(core);
      const tick = document.createElement('div');
      const tw = 16,
        th = 20;
      tick.style.cssText =
        'position:absolute;left:50%;top:' +
        (c - e.discR - th * 0.55) +
        'px;width:' +
        tw +
        'px;height:' +
        th +
        'px;margin-left:-' +
        tw / 2 +
        'px;filter:drop-shadow(0 3px 5px rgba(32,29,26,.22));';
      tick.innerHTML =
        '<svg width="' +
        tw +
        '" height="' +
        th +
        '" viewBox="0 0 16 20"><defs><linearGradient id="pinG' +
        e.R +
        '" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#8FEBC8" stop-opacity=".95"/><stop offset=".5" stop-color="#4DD5A3" stop-opacity=".8"/><stop offset="1" stop-color="#2FA97A" stop-opacity=".9"/></linearGradient></defs><path d="M8 19 L1.6 7.5 A7 7 0 1 1 14.4 7.5 Z" fill="url(#pinG' +
        e.R +
        ')" stroke="rgba(255,255,255,.75)" stroke-width="1"/><ellipse cx="5.6" cy="4.6" rx="3" ry="1.8" transform="rotate(-32 5.6 4.6)" fill="rgba(255,255,255,.55)"/></svg>';
      e.layer.appendChild(tick);
      this.applyDartStep(e, true);
    } else {
      this.buildQuad(e);
    }
  }

  makeWord(e, text) {
    const s = document.createElement('div');
    s.textContent = text;
    s.style.cssText =
      'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:' +
      e.wordFs +
      'px;font-weight:500;letter-spacing:.18em;text-indent:.18em;color:#2E7D4F;white-space:nowrap;will-change:transform;';
    return s;
  }

  applyOrbital(e) {
    const rot = this.rm ? 0 : this.orbitRot;
    e.orbit.style.transform = 'rotate(' + rot + 'deg)';
    e.orbitLabels.forEach((t, i) => {
      const active = i === this.stage;
      t.style.transform =
        'translate(-50%,-50%) rotate(' +
        -(rot + i * 90) +
        'deg) scale(' +
        (active ? 1.12 : 1) +
        ')';
      t.style.color = active ? '#2E7D4F' : '#8A857A';
      t.style.opacity = active ? '1' : '0.55';
    });
  }

  swapWord(e, word) {
    const show = e.wordA.style.opacity === '0' ? e.wordA : e.wordB;
    const hide = show === e.wordA ? e.wordB : e.wordA;
    show.textContent = word;
    show.style.opacity = '1';
    hide.style.opacity = '0';
  }

  resetArc(e) {
    e.arc.style.transition = 'none';
    e.arc.style.strokeDashoffset = e.arcC;
    void e.arc.getBoundingClientRect();
    if (!this.rm)
      e.arc.style.transition =
        'stroke-dashoffset .9s cubic-bezier(.25,.8,.3,1)';
    e.wrapped = false;
  }

  applyArcStep(e) {
    if (e.timer) {
      clearTimeout(e.timer);
      e.timer = null;
    }
    this.swapWord(e, STAGES[this.stage]);
    const cs = this.cycleSteps();
    if (e.wrapped) this.resetArc(e);
    e.arc.style.strokeDashoffset = e.arcC * (1 - cs / 4);
    if (cs === 4) {
      e.wrapped = true;
      e.timer = setTimeout(
        () => {
          this.pulse(e);
          e.timer = setTimeout(() => this.resetArc(e), 700);
        },
        this.rm ? 100 : 900,
      );
    }
  }

  applyDartStep(e, init) {
    if (!this.rm && !init) e.spinRot = (e.spinRot ?? 0) - 360 - 90;
    else if (e.spinRot === undefined) e.spinRot = this.rm ? 0 : this.orbitRot;
    e.board.style.transform = 'rotate(' + (this.rm ? 0 : e.spinRot) + 'deg)';
    e.dartSectors.forEach((p, i) =>
      p.setAttribute(
        'fill',
        i === this.stage
          ? 'rgba(62,155,99,.14)'
          : i % 2
            ? '#F4F1E9'
            : '#FBFAF7',
      ),
    );
    e.dartLabels.forEach((t, i) => {
      const a = i === this.stage;
      t.setAttribute('fill', a ? '#2E7D4F' : '#8A857A');
      t.setAttribute('opacity', a ? '1' : '0.55');
      t.setAttribute('font-weight', a ? '600' : '400');
    });
    if (!init && !this.rm) {
      const rip = document.createElement('div');
      rip.style.cssText =
        'position:absolute;inset:0;border-radius:50%;border:1px solid #3E9B63;pointer-events:none;will-change:transform,opacity;';
      e.layer.appendChild(rip);
      rip.animate(
        [
          { transform: 'scale(.88)', opacity: 0.5 },
          { transform: 'scale(1.18)', opacity: 0 },
        ],
        { duration: 800, easing: 'cubic-bezier(.25,.8,.3,1)' },
      ).onfinish = () => rip.remove();
    }
  }

  sectorPath(c, r, a0, a1) {
    const p = (a) => {
      const t = (a * Math.PI) / 180;
      return (
        (c + r * Math.sin(t)).toFixed(2) +
        ' ' +
        (c - r * Math.cos(t)).toFixed(2)
      );
    };
    return (
      'M' +
      c +
      ' ' +
      c +
      ' L' +
      p(a0) +
      ' A' +
      r +
      ' ' +
      r +
      ' 0 ' +
      (a1 - a0 > 180 ? 1 : 0) +
      ' 1 ' +
      p(a1) +
      ' Z'
    );
  }

  buildQuad(e) {
    const NS = 'http://www.w3.org/2000/svg';
    const cx = e.discR,
      d2 = e.discR * 2,
      off = e.R - e.discR;
    const svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('width', d2);
    svg.setAttribute('height', d2);
    svg.style.cssText =
      'position:absolute;left:' + off + 'px;top:' + off + 'px;';
    const cid = 'qclip' + e.R;
    const defs = document.createElementNS(NS, 'defs');
    const clip = document.createElementNS(NS, 'clipPath');
    clip.setAttribute('id', cid);
    const cc = document.createElementNS(NS, 'circle');
    cc.setAttribute('cx', cx);
    cc.setAttribute('cy', cx);
    cc.setAttribute('r', e.discR - 1);
    clip.appendChild(cc);
    defs.appendChild(clip);
    svg.appendChild(defs);
    const g = document.createElementNS(NS, 'g');
    g.setAttribute('clip-path', 'url(#' + cid + ')');
    svg.appendChild(g);
    e.sectors = [0, 1, 2, 3].map((i) => {
      const p = document.createElementNS(NS, 'path');
      p.setAttribute(
        'd',
        this.sectorPath(cx, e.discR + 2, i * 90, (i + 1) * 90),
      );
      p.setAttribute('fill', 'rgba(62,155,99,0)');
      g.appendChild(p);
      return p;
    });
    e.wedge = document.createElementNS(NS, 'path');
    e.wedge.setAttribute('fill', 'rgba(62,155,99,.16)');
    g.appendChild(e.wedge);
    const lines = document.createElementNS(NS, 'path');
    lines.setAttribute('d', 'M' + cx + ' 0V' + d2 + 'M0 ' + cx + 'H' + d2);
    lines.setAttribute('stroke', '#D8D3C8');
    lines.setAttribute('stroke-width', '1');
    lines.setAttribute('fill', 'none');
    g.appendChild(lines);
    const core = document.createElementNS(NS, 'circle');
    core.setAttribute('cx', cx);
    core.setAttribute('cy', cx);
    core.setAttribute('r', e.coreR);
    core.setAttribute('fill', '#FBFAF7');
    g.appendChild(core);
    e.qLabels = [0, 1, 2, 3].map((i) => {
      const t = document.createElementNS(NS, 'text');
      const a = ((i * 90 + 45) * Math.PI) / 180;
      t.setAttribute('x', (cx + e.qRad * Math.sin(a)).toFixed(1));
      t.setAttribute(
        'y',
        (cx - e.qRad * Math.cos(a) + e.qFs * 0.36).toFixed(1),
      );
      t.setAttribute('text-anchor', 'middle');
      t.setAttribute('font-size', e.qFs);
      t.setAttribute('letter-spacing', '.1em');
      t.setAttribute('font-family', "ui-monospace,'SF Mono',Menlo,monospace");
      t.textContent = STAGES[i];
      g.appendChild(t);
      return t;
    });
    e.layer.appendChild(svg);
    this.paintQuad(e, e.sweepVal);
  }

  paintQuad(e, val) {
    const full = Math.floor(val + 1e-6);
    e.sectors.forEach((s, i) =>
      s.setAttribute(
        'fill',
        i < full ? 'rgba(62,155,99,.10)' : 'rgba(62,155,99,0)',
      ),
    );
    const frac = val - full;
    e.wedge.setAttribute(
      'd',
      frac > 0.003 && full < 4
        ? this.sectorPath(e.discR, e.discR + 2, full * 90, (full + frac) * 90)
        : 'M0 0',
    );
    e.qLabels.forEach((t, i) => {
      const active = i === this.stage;
      t.setAttribute('fill', active ? '#2E7D4F' : '#8A857A');
      t.setAttribute('opacity', active ? '1' : '0.55');
      t.setAttribute('font-weight', active ? '600' : '400');
    });
  }

  applyQuadStep(e) {
    if (e.timer) {
      clearTimeout(e.timer);
      e.timer = null;
    }
    const cs = this.cycleSteps();
    if (cs <= e.sweepVal) {
      e.sweepVal = 0;
    }
    if (this.rm) {
      e.sweepVal = e.sweepFrom = e.sweepTarget = cs;
      this.paintQuad(e, cs);
      if (cs === 4)
        e.timer = setTimeout(() => {
          e.sweepVal = e.sweepFrom = e.sweepTarget = 0;
          this.paintQuad(e, 0);
        }, 600);
      return;
    }
    e.sweepFrom = e.sweepVal;
    e.sweepTarget = cs;
    e.sweepStart = performance.now();
  }

  tickQuad(e, now) {
    if (
      e.builtMode !== 'Quadrant sweep' ||
      this.rm ||
      e.sweepVal === e.sweepTarget
    )
      return;
    const t = Math.min(1, (now - e.sweepStart) / 800);
    const k = 1 - Math.pow(1 - t, 3);
    e.sweepVal = e.sweepFrom + (e.sweepTarget - e.sweepFrom) * k;
    this.paintQuad(e, e.sweepVal);
    if (t >= 1) {
      e.sweepVal = e.sweepTarget;
      if (e.sweepTarget === 4) {
        this.pulse(e);
        e.timer = setTimeout(() => {
          e.sweepVal = e.sweepFrom = e.sweepTarget = 0;
          this.paintQuad(e, 0);
        }, 700);
      }
    }
  }

  pulse(e) {
    if (this.rm || !e.disc || !e.disc.isConnected) return;
    e.disc.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.05)', offset: 0.4 },
        { transform: 'scale(1)' },
      ],
      { duration: 700, easing: 'ease-in-out' },
    );
  }

  stepStage() {
    this.totalSteps += 1;
    this.stage = this.totalSteps % 4;
    this.orbitRot -= 90;
    ['A', 'M'].forEach((k) => {
      const e = this.engines[k];
      if (!e.builtMode || !e.layer || !e.layer.isConnected) return;
      if (e.builtMode === 'Orbital dial') this.applyOrbital(e);
      else if (e.builtMode === 'Progress arc') this.applyArcStep(e);
      else if (e.builtMode === 'Dartboard spin') this.applyDartStep(e);
      else this.applyQuadStep(e);
    });
  }

  buildLayer(key, el, small) {
    if (!el || this.layers[key] === el) return;
    this.layers[key] = el;
    el.innerHTML = '';
    this.chips[key] = DATA.map((d) => this.makeChip(d, small));
    this.chips[key].forEach((c) => el.appendChild(c.el));
  }

  makeChip(d, small) {
    const el = document.createElement('div');
    el.style.cssText =
      'position:absolute;left:0;top:0;display:flex;align-items:center;white-space:nowrap;will-change:transform;opacity:0;background:#fffdf9;border:1px solid #d8d2c9;border-radius:999px;font-family:"IBM Plex Mono",monospace;color:#201d1a;box-shadow:0 4px 14px rgba(32,29,26,0.08);' +
      (small
        ? 'gap:6px;padding:5px 11px;font-size:11px;letter-spacing:0.4px;'
        : 'gap:7px;padding:7px 14px;font-size:12px;letter-spacing:0.5px;');
    const flag = document.createElement('img');
    const code = [...d.flag]
      .map((ch) => ch.codePointAt(0).toString(16))
      .join('-');
    flag.src =
      'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/' +
      code +
      '.svg';
    flag.style.cssText =
      'width:' +
      (small ? 13 : 15) +
      'px;height:' +
      (small ? 13 : 15) +
      'px;display:block;';
    flag.alt = '';
    const name = document.createElement('span');
    name.textContent = d.name;
    const dot = document.createElement('span');
    dot.style.cssText =
      'width:' +
      (small ? 6 : 7) +
      'px;height:' +
      (small ? 6 : 7) +
      'px;border-radius:50%;background:#1f8a5b;display:none;';
    el.appendChild(flag);
    el.appendChild(name);
    el.appendChild(dot);
    return { el, dot };
  }

  rgba(hex, a) {
    const n = parseInt(hex.replace('#', ''), 16);
    return (
      'rgba(' +
      ((n >> 16) & 255) +
      ',' +
      ((n >> 8) & 255) +
      ',' +
      (n & 255) +
      ',' +
      a +
      ')'
    );
  }
  ease(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }
  easeOut(t) {
    return 1 - Math.pow(1 - t, 3);
  }
  mod(a, n) {
    return ((a % n) + n) % n;
  }
  bez(x0, y0, x1, y1, x2, y2, x3, y3, t) {
    const u = 1 - t;
    return [
      u * u * u * x0 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x3,
      u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y3,
    ];
  }

  applyGlow(c, lt, tA, accent, s) {
    if (lt >= tA) {
      const k = Math.min(1, (lt - tA) / 0.3);
      const flash = 1 + 1.2 * (1 - k);
      c.el.style.borderColor = accent;
      c.el.style.boxShadow =
        '0 0 0 ' +
        3 * flash +
        'px ' +
        this.rgba(accent, 0.16 * flash) +
        ', 0 4px ' +
        18 * flash +
        'px ' +
        this.rgba(accent, 0.38 * Math.min(1, flash));
      return s * (1 + 0.16 * (1 - this.easeOut(k)));
    }
    c.el.style.borderColor = '#d8d2c9';
    c.el.style.boxShadow = '0 4px 14px rgba(32,29,26,0.08)';
    return s;
  }

  frame(now) {
    const dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    const speed = +(this.props.speed ?? 1.2);
    if (!this.props.paused) this.clock += dt * speed;
    const accent = this.props.accent ?? '#1F8A5B';
    ['A', 'M'].forEach((k) => {
      const e = this.engines[k];
      if (e.layer && e.slot && e.disc && e.builtMode !== this.mode)
        this.buildEngine(e);
      this.tickQuad(e, now);
    });
    this.frameA(accent);
    this.frameM(accent);
  }

  frameA(accent) {
    const layer = this.layers.A;
    if (!layer || !layer.isConnected) return;
    let hubActive = false;
    this.chips.A.forEach((c, i) => {
      const d = DATA[i];
      const lt = this.mod(this.clock - i * 2, 16);
      const prev = c.prev === undefined ? lt : c.prev;
      c.prev = lt;
      if (prev < 3.1 && lt >= 3.1 && lt - prev < 1) this.stepStage();
      if (lt >= 7.4) {
        c.el.style.opacity = '0';
        c.dot.style.display = 'none';
        return;
      }
      let x = 250,
        y = LYL[d.from],
        s = 1;
      let o = Math.min(1, lt / 0.35);
      if (lt < 2.4) {
        const u = this.ease(lt / 2.4);
        [x, y] = this.bez(
          250,
          LYL[d.from],
          360,
          LYL[d.from],
          420,
          262,
          556,
          262,
          u,
        );
        s = 0.85 + 0.15 * Math.min(1, lt / 0.5);
      } else if (lt < 3.8) {
        hubActive = true;
        const p = (lt - 2.4) / 1.4;
        x = 556 + 48 * p;
        y = 262;
        s = 1 + 0.04 * Math.sin((lt - 2.4) * 6);
        if (lt > 3.1) {
          c.dot.style.display = 'inline-block';
          c.dot.style.background = accent;
        }
      } else if (lt < 6.2) {
        const u = this.ease((lt - 3.8) / 2.4);
        [x, y] = this.bez(604, 262, 760, 262, 820, LY[d.to], 910, LY[d.to], u);
      } else {
        const v = (lt - 6.2) / 1.2;
        x = 910 + 100 * this.easeOut(v);
        y = LY[d.to];
        o = v < 0.55 ? 1 : 1 - (v - 0.55) / 0.45;
      }
      s = this.applyGlow(c, lt, 3.1, accent, s);
      c.el.style.opacity = String(o);
      c.el.style.transform =
        'translate(' +
        x +
        'px,' +
        y +
        'px) translate(-50%,-50%) scale(' +
        s +
        ')';
    });
    if (this.hubGlow && this.hubGlow.isConnected)
      this.hubGlow.style.opacity = hubActive ? '0.95' : '0.3';
  }

  frameM(accent) {
    const layer = this.layers.M;
    if (!layer || !layer.isConnected) return;
    const FX = MFX,
      FYB = MFYB,
      TX = MTX,
      TYC = MTYC;
    let hubActive = false;
    this.chips.M.forEach((c, i) => {
      const d = DATA[i];
      const lt = this.mod(this.clock - i * 2, 16);
      const prev = c.prev === undefined ? lt : c.prev;
      c.prev = lt;
      if (prev < 2.8 && lt >= 2.8 && lt - prev < 1) this.stepStage();
      if (lt >= 7.0) {
        c.el.style.opacity = '0';
        c.dot.style.display = 'none';
        return;
      }
      let x = FX[d.from],
        y = FYB[d.from],
        s = 1;
      let o = Math.min(1, lt / 0.35);
      if (lt < 2.2) {
        const u = this.ease(lt / 2.2);
        [x, y] = this.bez(
          FX[d.from],
          FYB[d.from],
          FX[d.from],
          190,
          200,
          215,
          200,
          292,
          u,
        );
        s = 0.85 + 0.15 * Math.min(1, lt / 0.5);
      } else if (lt < 3.6) {
        hubActive = true;
        const p = (lt - 2.2) / 1.4;
        x = 200;
        y = 292 + 20 * p;
        s = 1 + 0.04 * Math.sin((lt - 2.2) * 6);
        if (lt > 2.9) {
          c.dot.style.display = 'inline-block';
          c.dot.style.background = accent;
        }
      } else if (lt < 5.8) {
        const u = this.ease((lt - 3.6) / 2.2);
        [x, y] = this.bez(
          200,
          312,
          200,
          400,
          TX[d.to],
          430,
          TX[d.to],
          TYC[d.to],
          u,
        );
      } else {
        const v = (lt - 5.8) / 1.2;
        x = TX[d.to];
        y = TYC[d.to];
        o = v < 0.55 ? 1 : 1 - (v - 0.55) / 0.45;
      }
      s = this.applyGlow(c, lt, 2.9, accent, s);
      c.el.style.opacity = String(o);
      c.el.style.transform =
        'translate(' +
        x +
        'px,' +
        y +
        'px) translate(-50%,-50%) scale(' +
        s +
        ')';
    });
    if (this.hubGlowM && this.hubGlowM.isConnected)
      this.hubGlowM.style.opacity = hubActive ? '0.95' : '0.3';
  }

  start() {
    this.mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.rm = this.mq.matches;
    this.mqFn = (ev) => {
      this.rm = ev.matches;
      ['A', 'M'].forEach((k) => {
        const e = this.engines[k];
        if (e.layer && e.slot && e.disc) this.buildEngine(e);
      });
    };
    this.mq.addEventListener('change', this.mqFn);
    if (this.raf) cancelAnimationFrame(this.raf);
    this.last = performance.now();
    const loop = (now) => {
      this.frame(now);
      this.raf = requestAnimationFrame(loop);
    };
    this.raf = requestAnimationFrame(loop);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.mq) this.mq.removeEventListener('change', this.mqFn);
  }
}

const pill = {
  position: 'absolute',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#fffdf9',
  border: '1px solid #d8d2c9',
  borderRadius: 999,
  color: '#201d1a',
};

const StaticChip = ({
  left,
  top,
  width,
  height,
  gap,
  fontSize,
  letterSpacing,
  color,
  label,
  arrow,
}) => (
  <div
    style={{ ...pill, left, top, width, height, gap, fontSize, letterSpacing }}
  >
    {color ? (
      <span
        style={{
          width: gap,
          height: gap,
          borderRadius: '50%',
          background: color,
        }}
      />
    ) : null}
    {label}
    {arrow ? <span style={{ color: '#99928a' }}>→</span> : null}
  </div>
);

const Connectors = ({ width, height, paths }) => (
  <svg
    width={width}
    height={height}
    viewBox={`0 0 ${width} ${height}`}
    style={{ position: 'absolute', inset: 0 }}
    fill='none'
  >
    {paths.map((d, i) => (
      <path key={i} d={d} stroke='#d8d2c9' strokeWidth='1.5' />
    ))}
  </svg>
);

const EngineCore = ({
  w,
  h,
  setDisc,
  setEngine,
  setSlot,
  ring,
  disc,
  engine,
  coreBox,
  logoW,
  logoH,
  slotMt,
  slotW,
  slotH,
}) => (
  <>
    <div
      style={{
        position: 'absolute',
        ...ring,
        boxSizing: 'border-box',
        borderRadius: '50%',
        border: '1px dashed #D8D3C8',
      }}
    />
    <div
      ref={setDisc}
      style={{
        position: 'absolute',
        ...disc,
        boxSizing: 'border-box',
        borderRadius: '50%',
        background: '#FBFAF7',
        border: '1px solid #E3DFD5',
      }}
    />
    <div
      ref={setEngine}
      style={{
        position: 'absolute',
        ...engine,
        pointerEvents: 'none',
        fontFamily: FONT_MONO,
      }}
    />
    <div
      style={{
        position: 'absolute',
        ...engine,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: coreBox.gap,
        pointerEvents: 'none',
        fontFamily: FONT_MONO,
      }}
    >
      <svg
        width={logoW}
        height={logoH}
        viewBox='0 0 520 404'
        fill='none'
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
      >
        <path d={ENGINE_LOGO} fill='#4DD5A3' />
      </svg>
      <div
        ref={setSlot}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translateX(-50%)',
          marginTop: slotMt,
          width: slotW,
          height: slotH,
        }}
      />
    </div>
  </>
);

const PathfinderHero = ({
  engine = 'Quadrant sweep',
  speed = 1.2,
  paused = false,
  accent = '#1F8A5B',
  showCopy = true,
  audience: controlledAudience,
  onAudienceChange,
}) => {
  const t = useT();
  const { lang } = useLang();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const isMobileHook = useIsMobile();
  const { showLoginModal } = useLoginRequiredModal();

  const homePathfinderUnderline = `${process.env.REACT_APP_ASSETS_URL}/Home_v2/img-home-pathfinder-underline.svg`;

  const [internalAudience, setInternalAudience] = useState('user');
  const audience = controlledAudience ?? internalAudience;
  const [visualAudience, setVisualAudience] = useState(audience);
  const audienceTransitionTimerRef = useRef(null);

  useEffect(() => {
    setVisualAudience(audience);
  }, [audience]);

  useEffect(
    () => () => {
      if (audienceTransitionTimerRef.current) {
        clearTimeout(audienceTransitionTimerRef.current);
      }
    },
    [],
  );

  const handleAudienceChange = (nextAudience) => {
    if (nextAudience === visualAudience) return;

    setVisualAudience(nextAudience);
    if (audienceTransitionTimerRef.current) {
      clearTimeout(audienceTransitionTimerRef.current);
    }

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    audienceTransitionTimerRef.current = setTimeout(() => {
      if (controlledAudience == null) {
        setInternalAudience(nextAudience);
      }
      onAudienceChange?.(nextAudience);
    }, prefersReducedMotion ? 0 : 300);
  };
  const activeAudienceIndex = AUDIENCES.findIndex(
    ({ key }) => key === visualAudience,
  );

  const [w, setW] = useState(1200);
  const engRef = useRef(null);
  if (!engRef.current) engRef.current = new PipelineEngine();
  const eng = engRef.current;

  eng.props = { engine, speed, paused, accent };

  const roRef = useRef(null);
  const measureRef = useCallback((el) => {
    if (roRef.current) {
      roRef.current.disconnect();
      roRef.current = null;
    }
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const cw = el.clientWidth;
      if (cw > 0) setW((prev) => (Math.abs(cw - prev) > 1 ? cw : prev));
    });
    ro.observe(el);
    roRef.current = ro;
  }, []);

  const refFns = useMemo(
    () => ({
      setLayerA: (el) => eng.buildLayer('A', el, false),
      setLayerM: (el) => eng.buildLayer('M', el, true),
      setHubGlow: (el) => {
        eng.hubGlow = el;
      },
      setHubGlowM: (el) => {
        eng.hubGlowM = el;
      },
      setDiscA: (el) => eng.setEnginePart('A', 'disc', el),
      setDiscM: (el) => eng.setEnginePart('M', 'disc', el),
      setEngineA: (el) => eng.setEnginePart('A', 'layer', el),
      setEngineM: (el) => eng.setEnginePart('M', 'layer', el),
      setSlotA: (el) => eng.setEnginePart('A', 'slot', el),
      setSlotM: (el) => eng.setEnginePart('M', 'slot', el),
    }),
    [eng],
  );

  useEffect(() => {
    if (audience !== 'user') {
      eng.stop();
      return undefined;
    }

    eng.start();
    return () => eng.stop();
  }, [audience, eng]);

  const isMobile = w < 700;
  const dScale = Math.min(1, w / 1160);
  const mScale = Math.min(1.15, w / 400);
  const dWrapH = Math.round(520 * dScale);
  const mWrapH = Math.round(600 * mScale);

  return (
    <section className='relative overflow-hidden box-border flex flex-col items-center justify-center pt-[30px] pb-[80px] tabletH:py-10 tabletV:py-[30px] desktopL:pt-[60px] desktopL:pb-0 gap-10 tabletH:gap-6 tabletV:gap-5 desktopL:gap-10'>
      {/* width probe */}
      <div className='w-full'>
        <div ref={measureRef} style={{ width: '100%', height: 0 }} />

        {showCopy && (
          <div
            className='w-full gap-[28px] mobileMini:gap-8 tabletH:gap-5 tabletV:gap-5 desktopL:gap-[28px] desktopXXL:gap-8 px-4 tabletH:px-6 tabletV:px-10 desktopL:px-[80px]'
            style={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <div
              role='tablist'
              className='bg-grayscale-gray02/50 flex w-full mobileMini:max-w-[328px] mobile:max-w-[320px] tabletH:max-w-[440px] tabletV:max-w-[542px] desktopL:max-w-[628px] desktopXXL:max-w-[800px] 
              p-0.5 tabletV:p-[3px] desktopL:p-1 rounded-full box-border'
            >
              <div className='relative flex w-full gap-1'>
                <span
                  aria-hidden
                  className='pointer-events-none absolute inset-y-0 left-0 rounded-full bg-grayscale-gray00 shadow-3 transition-transform duration-300 ease-in-out motion-reduce:transition-none'
                  style={{
                    width: `calc((100% - ${(AUDIENCES.length - 1) * 4}px) / ${AUDIENCES.length})`,
                    transform: `translateX(calc(${activeAudienceIndex * 100}% + ${activeAudienceIndex * 4}px))`,
                  }}
                />

                {AUDIENCES.map((a) => {
                  const active = visualAudience === a.key;
                  const disabled = Boolean(a.disabled);
                  return (
                    <BaseButton
                      key={a.key}
                      type='text'
                      size='s'
                      typo={false}
                      hideIcon
                      isStatic
                      role='tab'
                      aria-selected={active}
                      aria-disabled={disabled}
                      tabIndex={disabled ? -1 : undefined}
                      onClick={() => {
                        if (disabled) return;
                        if (a.href) {
                          goToAudienceLink(a);
                          return;
                        }
                        handleAudienceChange(a.key);
                      }}
                      className={`relative z-[1] h-auto min-w-0 flex-1 rounded-full border-0 bg-transparent p-1.5 text-center transition-colors duration-200 motion-reduce:transition-none tabletV:p-2 desktopXXL:px-3 desktopXXL:py-2.5 typo-b12Strong tabletH:typo-b13Strong desktopL:typo-b14Strong ${
                        active ? 'text-text-primary' : 'text-text-secondary'
                      } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      {t(a.label)}
                    </BaseButton>
                  );
                })}
              </div>
            </div>

            <div className={audience === 'user' ? 'contents' : 'hidden'}>
              <div className='flex flex-col items-center gap-3 w-full'>
                <span className='mobileMini:typo-h6 mobile:typo-h5 tabletH:typo-h6 tabletV:typo-h5 desktopL:typo-h4 desktopXXL:typo-h2 text-grayscale-gray05'>
                  {lang === 'kr' ? (
                    <>
                      <span className='relative inline-block px-1'>
                        <img
                          src={homePathfinderUnderline}
                          alt=''
                          aria-hidden
                          className='pointer-events-none absolute left-1/2 -bottom-[0.08em] z-0 h-auto w-[calc(92%)] max-w-none -translate-x-1/2 select-none'
                        />
                        <span className='relative z-[1] text-grayscale-gray09 italic'>
                          Pathfinder
                        </span>
                      </span>
                      와 함께{' '}
                      <span className='text-grayscale-gray09'>
                        한국 커리어
                        <br /> 여정
                      </span>
                      을 시작하세요<span>.</span>
                    </>
                  ) : (
                    <>
                      Begin your{' '}
                      <span className='text-grayscale-gray09'>
                        Korean career
                        <br /> journey
                      </span>{' '}
                      with{' '}
                      <span className='relative inline-block px-1'>
                        <img
                          src={homePathfinderUnderline}
                          alt=''
                          aria-hidden
                          className='pointer-events-none absolute left-1/2 -bottom-[0.08em] z-0 h-auto w-[calc(92%)] max-w-none -translate-x-1/2 select-none'
                        />
                        <span className='relative z-[1] text-grayscale-gray09 italic'>
                          Pathfinder
                        </span>
                      </span>
                      <span>.</span>
                    </>
                  )}
                </span>
                <p
                  className='text-[11px] mobile:text-[12px] tabletH:text-[12px] tabletV:text-[14px] desktopL:text-[16px] desktopXXL:text-[16px]'
                  style={{
                    color: 'var(--Color-text-txt-secondary, #686561)',
                    textAlign: 'center',
                    fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
                    fontWeight: 400,
                    lineHeight: '150%',
                  }}
                >
                  {t(
                    'Based on your responses,',
                    '응답을 바탕으로,',
                  )}{' '}
                  <span className='block tabletH:hidden' />
                  {t(
                    "here's how we evaluated your profile.",
                    '프로필을 이렇게 평가했어요.',
                  )}
                </p>
              </div>

              <div className='flex items-start gap-2 w-full tabletH:w-fit'>
                <BaseButton
                  type={isMobileHook ? 'primary' : 'text_basic'}
                  onClick={() => {
                    trackCtaClick({
                      button_name: 'start_pathfinder',
                      section: 'hero',
                      page_path: window.location.pathname,
                      destination: '/pathfinder',
                    });
                    if (!isLoggedIn) {
                      showLoginModal('pathfinder_cta');
                      return;
                    }
                    navigate('/pathfinder?source=pathfinder_cta');
                  }}
                  icon={<ArrowRightIcon />}
                  iconPosition='after'
                  gap='gap-1'
                  bgclass='pr-2'
                  width='100%'
                  textWrapperClass='flex-none'
                >
                  {t('Get started')}
                </BaseButton>

                <div className='hidden tabletH:block'>
                  <GoogleButton isHome={true} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Desktop pipeline ── */}
      {audience === 'user' && !isMobile && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: dWrapH,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: 1160,
              height: 520,
              flex: 'none',
              transform: `scale(${dScale.toFixed(4)})`,
              transformOrigin: 'top center',
            }}
          >
            <Connectors width={1160} height={520} paths={D_PATHS} />

            {D_TRAITS.map((t, i) => (
              <StaticChip
                key={`dt${i}`}
                left={50}
                top={t.top}
                width={200}
                height={44}
                gap={9}
                fontSize={12}
                letterSpacing='0.3px'
                color={t.color}
                label={t.label}
              />
            ))}
            {D_OUTCOMES.map((o, i) => (
              <StaticChip
                key={`do${i}`}
                left={910}
                top={o.top}
                width={200}
                height={44}
                gap={9}
                fontSize={13}
                letterSpacing='0.5px'
                label={o.label}
                arrow
              />
            ))}

            <div
              ref={refFns.setHubGlow}
              style={{
                position: 'absolute',
                left: 448,
                top: 130,
                width: 264,
                height: 264,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(31,138,91,0.16) 0%, rgba(31,138,91,0) 68%)',
                opacity: 0.3,
                transition: 'opacity 0.4s',
              }}
            />
            <EngineCore
              setDisc={refFns.setDiscA}
              setEngine={refFns.setEngineA}
              setSlot={refFns.setSlotA}
              ring={{ left: 484, top: 166, width: 192, height: 192 }}
              disc={{ left: 494, top: 176, width: 172, height: 172 }}
              engine={{ left: 484, top: 166, width: 192, height: 192 }}
              coreBox={{ gap: 8 }}
              logoW={46}
              logoH={36}
              slotMt={26}
              slotW={108}
              slotH={14}
            />

            <div
              ref={refFns.setLayerA}
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            />
          </div>
        </div>
      )}

      {/* ── Mobile pipeline ── */}
      {audience === 'user' && isMobile && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: mWrapH,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: 400,
              height: 600,
              flex: 'none',
              transform: `scale(${mScale.toFixed(4)})`,
              transformOrigin: 'top center',
            }}
          >
            <Connectors width={400} height={600} paths={M_PATHS} />

            {M_TRAITS.map((t, i) => (
              <StaticChip
                key={`mt${i}`}
                left={t.left}
                top={t.top}
                width={150}
                height={36}
                gap={7}
                fontSize={10}
                letterSpacing='0.3px'
                color={t.color}
                label={t.label}
              />
            ))}
            {M_OUTCOMES.map((o, i) => (
              <StaticChip
                key={`mo${i}`}
                left={o.left}
                top={o.top}
                width={150}
                height={36}
                gap={7}
                fontSize={10}
                letterSpacing='0.5px'
                label={o.label}
                arrow
              />
            ))}

            <div
              ref={refFns.setHubGlowM}
              style={{
                position: 'absolute',
                left: 100,
                top: 200,
                width: 200,
                height: 200,
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(31,138,91,0.16) 0%, rgba(31,138,91,0) 68%)',
                opacity: 0.3,
                transition: 'opacity 0.4s',
              }}
            />
            <EngineCore
              setDisc={refFns.setDiscM}
              setEngine={refFns.setEngineM}
              setSlot={refFns.setSlotM}
              ring={{ left: 122, top: 222, width: 156, height: 156 }}
              disc={{ left: 130, top: 230, width: 140, height: 140 }}
              engine={{ left: 122, top: 222, width: 156, height: 156 }}
              coreBox={{ gap: 6 }}
              logoW={38}
              logoH={30}
              slotMt={22}
              slotW={90}
              slotH={12}
            />

            <div
              ref={refFns.setLayerM}
              style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default PathfinderHero;
